import { useForm } from '@mantine/form';
import {
  TextInput,
  NumberInput,
  Select,
  Button,
  Stack,
  Box,
  Group,
  Text,
  ActionIcon
} from '@mantine/core';
// Using native HTML5 date/time inputs instead of Mantine
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ProcessedBetSlipData } from '../../../backend/src/models/BetSlip';

interface BetSlipManualEntryProps {
  onSubmit: (data: ProcessedBetSlipData) => void;
}

type BetType = 'straight' | 'parlay' | 'teaser' | 'round_robin' | 'futures' | 'prop';
type MarketType = 'money_line' | 'point_spread' | 'total' | 'prop' | 'futures';

interface FormValues {
  ticketId: string;
  casinoName: string;
  wagerAmount: number;
  potentialPayout: number;
  betType: BetType;
  eventDateTime?: Date;
  selections: {
    sport: string;
    league: string;
    eventDateTime?: Date;
    homeTeam: string;
    awayTeam: string;
    selectedTeam: string;
    marketType: MarketType;
    line: number;
    odds: string;
  }[];
}

function combineDateAndTime(dateStr?: string, timeStr?: string): Date | undefined {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  if (timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    date.setHours(hours, minutes);
  }
  return date;
}

export function BetSlipManualEntry({ onSubmit }: BetSlipManualEntryProps) {
  const form = useForm<FormValues>({
    initialValues: {
      ticketId: '',
      casinoName: '',
      wagerAmount: 0,
      potentialPayout: 0,
      betType: 'straight',
      eventDateTime: undefined,
      selections: [
        {
          sport: '',
          league: '',
          eventDateTime: undefined,
          homeTeam: '',
          awayTeam: '',
          selectedTeam: '',
          marketType: 'money_line',
          line: 0,
          odds: '',
        }
      ],
    },
    validate: {
      ticketId: (value) => (value ? null : 'Ticket ID is required'),
      casinoName: (value) => (value ? null : 'Casino/Sportsbook is required'),
      wagerAmount: (value) => (value > 0 ? null : 'Wager amount must be greater than 0'),
      selections: {
        sport: (value) => (value ? null : 'Sport is required'),
        homeTeam: (value) => (value ? null : 'Home team is required'),
        awayTeam: (value) => (value ? null : 'Away team is required'),
        odds: (value) => (value ? null : 'Odds are required'),
      },
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    const formElement = document.querySelector('form');
    const mainDateInput = formElement?.querySelector('input[type="date"]') as HTMLInputElement;
    const mainTimeInput = formElement?.querySelector('input[type="time"]') as HTMLInputElement;

    const processedData: ProcessedBetSlipData = {
      ...values,
      eventDateTime: combineDateAndTime(mainDateInput?.value, mainTimeInput?.value),
      selections: values.selections.map((selection, index) => {
        const selectionDateInput = formElement?.querySelector(`input[name="selections.${index}.date"]`) as HTMLInputElement;
        const selectionTimeInput = formElement?.querySelector(`input[name="selections.${index}.time"]`) as HTMLInputElement;
        return {
          ...selection,
          eventDateTime: combineDateAndTime(selectionDateInput?.value, selectionTimeInput?.value)
        };
      }),
      imageUrl: '', // No image for manual entry
      extractedText: '', // No OCR text for manual entry
    };
    onSubmit(processedData);
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="md">
        <Box>
          <Text size="sm" weight={500} mb={4}>Ticket ID</Text>
          <TextInput
            placeholder="Enter ticket ID"
            {...form.getInputProps('ticketId')}
          />
        </Box>

        <Box>
          <Text size="sm" weight={500} mb={4}>Casino/Sportsbook</Text>
          <TextInput
            placeholder="Enter casino or sportsbook name"
            {...form.getInputProps('casinoName')}
          />
        </Box>

        <Group grow>
          <Box>
            <Text size="sm" weight={500} mb={4}>Wager Amount</Text>
            <NumberInput
              placeholder="Enter wager amount"
              precision={2}
              min={0}
              {...form.getInputProps('wagerAmount')}
            />
          </Box>

          <Box>
            <Text size="sm" weight={500} mb={4}>Potential Payout</Text>
            <NumberInput
              placeholder="Enter potential payout"
              precision={2}
              min={0}
              {...form.getInputProps('potentialPayout')}
            />
          </Box>
        </Group>

        <Box>
          <Text size="sm" weight={500} mb={4}>Event Date/Time</Text>
          <Group grow>
            <input
              type="date"
              name="eventDateTime.date"
              style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
              defaultValue={new Date().toISOString().split('T')[0]}
            />
            <input
              type="time"
              name="eventDateTime.time"
              style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', marginLeft: '8px' }}
              defaultValue={new Date().toTimeString().substring(0,5)}
            />
          </Group>
        </Box>

        <Box>
          <Text size="sm" weight={500} mb={4}>Bet Type</Text>
          <Select
            data={[
              { value: 'straight', label: 'Straight Bet (Single)' },
              { value: 'parlay', label: 'Parlay (Multiple Selections)' },
              { value: 'teaser', label: 'Teaser' },
              { value: 'round_robin', label: 'Round Robin' },
              { value: 'futures', label: 'Futures' },
              { value: 'prop', label: 'Proposition (Prop)' }
            ]}
            {...form.getInputProps('betType')}
          />
        </Box>

        {/* Selections */}
        <Box>
          <Group position="apart" mb="xs">
            <Text weight={500}>Selections</Text>
            <ActionIcon
              color="blue"
              onClick={() => {
                form.insertListItem('selections', {
                  sport: '',
                  league: '',
                  eventDateTime: undefined,
                  homeTeam: '',
                  awayTeam: '',
                  selectedTeam: '',
                  marketType: 'money_line',
                  line: 0,
                  odds: '',
                });
              }}
            >
              <IconPlus size="1.2rem" />
            </ActionIcon>
          </Group>

          {form.values.selections.map((_, index) => (
            <Box 
              key={index} 
              sx={(theme) => ({
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                marginBottom: theme.spacing.sm,
              })}
            >
              <Group position="apart" mb="xs">
                <Text weight={500}>Selection {index + 1}</Text>
                {index > 0 && (
                  <ActionIcon
                    color="red"
                    onClick={() => form.removeListItem('selections', index)}
                  >
                    <IconTrash size="1.2rem" />
                  </ActionIcon>
                )}
              </Group>

              <Stack spacing="xs">
                <Box>
                  <Text size="sm" weight={500} mb={4}>Event Date/Time</Text>
                  <Group grow>
                    <input
                      type="date"
                      name={`selections.${index}.date`}
                      style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px' }}
                      defaultValue={new Date().toISOString().split('T')[0]}
                    />
                    <input
                      type="time"
                      name={`selections.${index}.time`}
                      style={{ padding: '6px', border: '1px solid #ced4da', borderRadius: '4px', marginLeft: '8px' }}
                      defaultValue={new Date().toTimeString().substring(0,5)}
                    />
                  </Group>
                </Box>

                <Box>
                  <Text size="sm" weight={500} mb={4}>Sport</Text>
                  <Select
                    data={[
                      { value: 'NFL', label: 'NFL Football' },
                      { value: 'NBA', label: 'NBA Basketball' },
                      { value: 'MLB', label: 'MLB Baseball' },
                      { value: 'NHL', label: 'NHL Hockey' },
                      { value: 'NCAAF', label: 'College Football' },
                      { value: 'NCAAB', label: 'College Basketball' }
                    ]}
                    {...form.getInputProps(`selections.${index}.sport`)}
                  />
                </Box>

                <Group grow>
                  <Box>
                    <Text size="sm" weight={500} mb={4}>Home Team</Text>
                    <TextInput
                      placeholder="Enter home team"
                      {...form.getInputProps(`selections.${index}.homeTeam`)}
                    />
                  </Box>

                  <Box>
                    <Text size="sm" weight={500} mb={4}>Away Team</Text>
                    <TextInput
                      placeholder="Enter away team"
                      {...form.getInputProps(`selections.${index}.awayTeam`)}
                    />
                  </Box>
                </Group>

                <Box>
                  <Text size="sm" weight={500} mb={4}>Market Type</Text>
                  <Select
                    data={[
                      { value: 'money_line', label: 'Money Line' },
                      { value: 'point_spread', label: 'Point Spread' },
                      { value: 'total', label: 'Total (Over/Under)' },
                      { value: 'prop', label: 'Proposition' },
                      { value: 'futures', label: 'Futures' }
                    ]}
                    {...form.getInputProps(`selections.${index}.marketType`)}
                  />
                </Box>

                {form.values.selections[index].marketType !== 'money_line' && (
                  <Box>
                    <Text size="sm" weight={500} mb={4}>Line</Text>
                    <NumberInput
                      placeholder="Enter point spread or total"
                      precision={1}
                      {...form.getInputProps(`selections.${index}.line`)}
                    />
                  </Box>
                )}

                <Box>
                  <Text size="sm" weight={500} mb={4}>Odds</Text>
                  <TextInput
                    placeholder="Enter odds (e.g. -110)"
                    {...form.getInputProps(`selections.${index}.odds`)}
                  />
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>

        <Button type="submit">Review Bet Slip</Button>
      </Stack>
    </form>
  );
}